/* @HEADER
 * @version 1.1.1441 | 2026-11-02
 * @purpose Captures the daily WIP snapshot for every board of the space just opened (ZVX-ANALYTICS-W2-03) -- fires once per activeSpaceId change, non-critical, errors swallowed. Moved out of App.tsx unchanged (ZVX-DEV-5944, campaign 3) as a hook AuthenticatedApp calls with the active space id.
 * @sidecar useDailyWipSnapshotOnSpace.ts.header.md
 * @layer app | @hex adapter | @ctx analytics
 * @public false
 * @edit careful
 */

// SpecRefs: ZVX-DEV-8868, ZVX-DEV-5944, ZVX-ANALYTICS-W2-03

import { useEffect } from 'react'
import { getDbFor } from '../lib/db'
import { resolveDbName } from '../lib/db/dbName'
import { getActiveProject } from '../lib/projectStore'
import { getActiveDataInOneTransaction } from '../lib/db/importVersions'
import { getBoardsByGroupIn } from '../lib/db/boardData'
import { getOverridesIn } from '../features/kanban/public-api'
import { captureDailyWipSnapshot } from '../features/kanban/dailyWipSnapshotStore'
import type { WipCapturableCard } from '../features/kanban/dailyWipSnapshotStore'

/**
 * ZVX-DEV-5944 -- AuthenticatedApp's WIP-snapshot effect, moved here as text:
 * same body, same `[activeSpaceId]` dependency list. Called where the effect
 * used to stand.
 */
export function useDailyWipSnapshotOnSpace(activeSpaceId: string | null): void {
  // ZVX-ANALYTICS-W2-03: capture daily WIP snapshot when a space is opened.
  // Fires once per unique activeSpaceId change. Non-critical — errors are swallowed.
  useEffect(() => {
    if (!activeSpaceId) return
    let cancelled = false
    async function captureForGroup() {
      // ZVX-DEV-8868 (ZVX-4805) -- ONE database, named here from the registry's active project (as the persistence port names it, not the ambient pointer, which
      // names the legacy database until the registry has answered), used for the boards of the space, the cards, the overrides and the write of the snapshot: the
      // answer cannot move between them. The cards are read in ONE readonly transaction with no lock: the locked `getActiveData()` read about fourteen stores of
      // the project for the sake of `data.tasks`, on every opening of a space, in the line every writer waits in.
      const dbName = resolveDbName(getActiveProject())
      const [boards, data, ovs] = await Promise.all([
        getBoardsByGroupIn(dbName, activeSpaceId!),
        getDbFor(dbName).then((db) => getActiveDataInOneTransaction(db)),
        getOverridesIn(dbName),
      ])
      if (cancelled || !data) return
      for (const board of boards) {
        const boardCards: WipCapturableCard[] = data.tasks
          .filter((t: { board_id?: string }) => (t.board_id ?? 'default') === board.id)
          .map((t: { task_id: string; status?: string; board_id?: string }) => ({ task: t }))
        await captureDailyWipSnapshot(board.id, boardCards, ovs, dbName)
      }
    }
    captureForGroup().catch(() => {/* non-critical — snapshot capture failure is silent */})
    return () => { cancelled = true }
  }, [activeSpaceId]) // eslint-disable-line react-hooks/exhaustive-deps
}
